import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
// @mui
import {
  Stack,
  Typography,
  Skeleton,
  Button,
  Avatar,
  Box,
  Collapse,
  Chip,
} from "@mui/material";
import { styled } from '@mui/material/styles';
import { LoadingButton } from '@mui/lab';
// hooks
import useResponsive from '../../../../hooks/useResponsive';
import useAuth from '../../../../hooks/useAuth';
import useIsMountedRef from '../../../../hooks/useIsMountedRef';
// form
import { useForm } from 'react-hook-form';
// components
import DIOText from '../../../../components/DIOText';
import Iconify from '../../../../components/Iconify';
import Label from '../../../../components/Label';
import SvgIconStyle from '../../../../components/SvgIconStyle';
import {
  PositionLabel,
  CrewRejectHistoryDialog,
} from "../../";
import {
  PositionGuideDialog,
  JobPostHelpDialog,
  NoDeveloperDialog,
  ReviewAgainDialog,
} from '../';
import { FormProvider, RHFRoleSelect } from '../../../../components/hook-form';
import { MotionViewport } from '../../../../components/animate';
// util
import axios from '../../../../utils/axios';
// constants
import { POSITION_OPTION_DICT } from '../../../../constants';


export default function SetDetailPositionForm({ jobPost, next, isEdit=false }) {
  const { user } = useAuth();
  const smUp = useResponsive('up', 'sm');
  const { enqueueSnackbar } = useSnackbar();
  const isMountedRef = useIsMountedRef();

  const [isLoading, setIsLoading] = useState(false);
  const [isSubmit, setIsSubmit] = useState(false);
  const [canSubmit, setCanSubmit] = useState(false);
  const [roleOption, setRoleOption] = useState([]);
  const [groupRoleOption, setGroupRoleOption] = useState([]);
  const [isOpenMeetingDialog, setIsOpenMeetingDialog] = useState(false);
  const [crewRejectHistoryOpen, setCrewRejectHistoryOpen] = useState(false);
  const [reviewAgainOpen, setReviewAgainOpen] = useState(false);

  // developer
  const [devPositionGroup, setDevPositionGroup] = useState(null);
  const [selectedDevPositionRole, setSelectedDevPositionRole] = useState(null);
  const [noDeveloperDialog, setNoDeveloperDialog] = useState(false);

  // 포지션 안내 다이얼로그
  const [isOpenPositionGuide, setIsOpenPositionGuide] = useState(false);

  const defaultValues = {
    role: jobPost?.detail_role?.[0] || [],
  }

  const methods = useForm({
    defaultValues: defaultValues,
  });

  const { watch, setError, getValues, setValue, handleSubmit } = methods;

  const watchValues = async (value, name, type) => {
    if (!isEdit && name === 'role' && value.role.length === 1 && value.role[0].can) {
      setIsOpenPositionGuide(true);
    }
    if (isEdit && name === 'role' &&
      (
        (value.role?.length === 1 && jobPost.detail_role?.id && value.role[0].id !== jobPost.detail_role?.id) // 변경
        || (value.role?.length === 1 && !jobPost.detail_role?.id) // 없다가 생김
        || (value.role?.length === 0 && jobPost.detail_role?.id) // 있다가 없어짐
      )
    ) {
      setReviewAgainOpen(true);
    }
    setCanSubmit(value.role?.length === 1);
  }

  const findDeveloperPosition = (groupRoles, detailRoleId) => {
    const groupRole = groupRoles.find((groupRole) => groupRole?.roles.find((role) => role.id === detailRoleId));
    const childGroupRole = groupRoles?.find((groupRole) => groupRole.children?.find((childGroupRole) => childGroupRole?.roles.find((role) => role.id === detailRoleId)));
    if (groupRole) {
      const devRole = groupRole.roles.find((role) => role.id === detailRoleId);
      setDevPositionGroup(groupRole);
      selectDevRole(devRole);
    } else if (childGroupRole) {
      const child = childGroupRole.children.find((childGroupRole) => childGroupRole.roles.find((role) => role.id === detailRoleId));
      const devRole = child.roles.find((role) => role.id === detailRoleId);
      setDevPositionGroup(childGroupRole);
      selectDevRole(devRole);
    }
  }

  const findOtherPosition = (roles, detailRoleId) => {
    const role = roles.find((role) => role.id === detailRoleId);
    if (role) {
      setValue('role', [role]);
    }
  }

  useEffect(() => {
    const initialize = async () => {
      try {
        setIsLoading(true);
        const companyId = user?.employee.company[0].id;
        const jobPostId = jobPost?.id;
        const rolesResponse = await axios.get(`api/company/${companyId}/job-posts/${jobPostId}/matching-role/`)
        setRoleOption(rolesResponse.data);

        const groupRolesResponse = await axios.get(`api/company/${companyId}/job-posts/${jobPostId}/group-role/`)
        setGroupRoleOption(groupRolesResponse.data);

        if (jobPost.position === 'developer' && jobPost.detail_role?.id) {
          findDeveloperPosition(groupRolesResponse.data, jobPost.detail_role?.id);
        } else {
          findOtherPosition(rolesResponse.data, jobPost.detail_role?.id);
        }
        setIsLoading(false);
      } catch (err) {
        setIsLoading(false);
        console.error(err);
      }
    };
    initialize();

    const subscription = watch((value, { name, type }) => watchValues(value, name, type ));
    return () => subscription.unsubscribe();
  }, [watch]);

  const roleWatch = watch('role');

  const makeJobPostData = async (data) => {
    const { role } = data;
    return {
      detail_role: role?.length ? role[0].id : null,
    }
  };

  const handleError = (error) => {
    console.error(error);
    if (error.response && error.response.status === 400) {
      setError('afterSubmit', { type: "manual", message: error.response.data.message});
    } else {
      setError('afterSubmit', { type: "manual", message: "저장에 실패하였습니다. 관리자에 문의해주세요."});
    }
  };

  const setDetailPosition = async (data) => {
    setIsSubmit(true);
    const companyId = user?.employee.company[0].id;
    try {
      const jobPostData = await makeJobPostData(data);
      const jobPostId = jobPost.id;
      await axios.post(`/api/company/${companyId}/job-posts/${jobPostId}/matching-role/`, jobPostData);
      if (smUp) {
        enqueueSnackbar('저장되었습니다');
      } else {
        enqueueSnackbar('저장되었습니다', {anchorOrigin: { vertical: 'bottom', horizontal: 'center' }});
      }
      await next(jobPostId);
      if (isMountedRef.current) {
        setIsSubmit(false);
      }
    } catch (error) {
      setIsSubmit(false);
      handleError(error);
    }
  }

  const openSetMeetingDialog = () => {
    setIsOpenMeetingDialog(true);
  };

  const selectDevPositionGroup = (value) => {
    setDevPositionGroup(value);
    setSelectedDevPositionRole(null);
    setValue('role', []);
  };

  const selectDevRole = (value) => {
    setSelectedDevPositionRole(value);
    setValue('role', [value]);
  }

  if (isLoading) {
    return (
      <>
      <Typography variant="h4"><Skeleton /></Typography>
      <Skeleton height={100} />
      <Skeleton height={100} />
      <Skeleton height={100} />
      </>
    )
  }

  return (
    <>
    <Stack direction="row" alignItems="center" justifyContent="space-between">
      {/* # Label */}
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1 }}>
        <Label variant="outlined" color="secondary" sx={{ borderRadius: 2}}>
          #원격 근무
        </Label>
        <Label variant="outlined" color="secondary" sx={{ borderRadius: 2}}>
          #파트타임
        </Label>
        <Label variant="outlined" color="secondary" sx={{ borderRadius: 2}}>
          #4주 단위
        </Label>
      </Stack>
      {/* help button at smUp */}
      <Stack direction="row" alignItems="center" spacing={1}>
        {smUp && jobPost.position !== 'developer' && !isEdit && (
        <Button
          variant="text"
          color="error"
          startIcon={<SvgIconStyle src={`/icons/ic_headphone.svg`} sx={{ width: 28, height: 28, color: 'error.main' }} />}
          onClick={openSetMeetingDialog}
        >
          공고 작성이 어려우신가요?
        </Button>
        )}
        {smUp && isEdit && jobPost.crew_reject_count > 0 && (
        <Button
          variant="text"
          color="error"
          startIcon={<Iconify icon={"eva:alert-triangle-fill"} sx={{ width: 28, height: 28, color: 'error.main' }} />}
          onClick={() => setCrewRejectHistoryOpen(true)}
          disableRipple
        >
          {jobPost.crew_reject_count}명의 매칭 거절 사유 확인하기
        </Button>
        )}
      </Stack>
    </Stack>
    <Stack direction="row" alignItems="center" sx={{ mb: 1 , flexWrap: 'wrap', gap: 0.5}}>
      <Typography variant="h4">
        어떤
      </Typography>
      <PositionLabel position={jobPost?.position} detailRole={jobPost?.detail_role} aiSelected={jobPost?.select_ai_position} />
      <Typography variant="h4">
        를 찾아드릴까요?
      </Typography>
    </Stack>

    {jobPost.position !== 'developer' && (
    <Typography variant="body2" sx={{ mb: 3, ...!smUp && { color: 'text.secondary' } }}>
      선택하신 세부 포지션으로 <DIOText />에서 한 명 매칭해드릴게요.
    </Typography>
    )}

    {jobPost.position !== 'developer' && (
    <FormProvider methods={methods}>
      <RHFRoleSelect
        name="role"
        position={jobPost.position}
        placeholder={(roleWatch && roleWatch.length > 0) ? smUp ? `${POSITION_OPTION_DICT[jobPost.position]}를 찾고 있어요.` : '' : "세부 포지션은 한 개만 선택할 수 있습니다."}
        options={roleOption}
      />
    </FormProvider>
    )}

    {jobPost.position === 'developer' && (
      <>
      <Box
        component={MotionViewport}
        sx={{
          my: 3,
          display: 'grid',
          gap: 2,
          gridTemplateColumns: {
            xs: 'repeat(3, 1fr)',
            sm: 'repeat(6, 1fr)',
          },
        }}
      >
      {/* dev detail position selector */}
      {groupRoleOption.map((group, index) => (
        <Button
          key={index}
          disableRipple
          variant={devPositionGroup?.name === group.name ? "outlined" : "text"}
          sx={{
            borderRadius: 2,
            px: { xs: 1, sm: 2 },
            py: { xs: 2, sm: 2.5 },
            boxShadow: (theme) => theme.customShadows.card,
            ...(devPositionGroup !== null && devPositionGroup?.name !== group.name && {
              color: "text.disabled",
              backgroundColor: "grey.200",
            })
          }}
          onClick={() => selectDevPositionGroup(group)}
        >
          <Stack justifyContent="center" alignItems="center">
            <Collapse
              in={devPositionGroup === null}
            >
              <Avatar
                src={group?.image_url}
                alt={group?.name}
                sx={{ mb: 2, width: 56, height: 56 }}
              />
            </Collapse>
            <Typography variant={smUp ? "subtitle1" : "subtitle2"}>
              {group.name}
            </Typography>
          </Stack>
        </Button>
      ))}
      </Box>

      {/* sub selector */}
      {devPositionGroup === null && (
      <Stack direction={"row"} alignItems={"flex-start"} spacing={1}>
        <Iconify icon={"eva:alert-triangle-fill"} sx={{ width: 24, height: 24, color: 'text.secondary' }} />
        <Typography variant="subtitle2" sx={{ color: "text.secondary" }}>
          풀스택 개발자는 채용이 어렵습니다. 프론트엔드/백엔드 각각 공고를 작성해주세요.
        </Typography>
      </Stack>
      )}
      {devPositionGroup?.children?.map((section, index) => (
        <Box key={`dpg-${index}`}>
          <Typography variant="h6">
            {section.name}
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {section.description}
          </Typography>
          <Box
            component={MotionViewport}
            sx={{
              mt: 2,
              mb: 5,
              display: 'grid',
              gap: 2,
              gridTemplateColumns: {
                xs: 'repeat(1, 1fr)',
              },
            }}
          >
          {section?.roles?.map((role, index) => (
            <DetailRoleButton
              selected={selectedDevPositionRole}
              role={role}
              key={`sdrb-${index}`}
              index={index}
              selectDevRole={selectDevRole}
            />
          ))}
          </Box>
        </Box>
      ))}
      {devPositionGroup?.roles?.length > 0 && (
      <Box
        component={MotionViewport}
        sx={{
          mt: 2,
          mb: 3,
          display: 'grid',
          gap: 2,
          gridTemplateColumns: {
            xs: 'repeat(1, 1fr)',
          },
        }}
      >
        {devPositionGroup?.roles?.map((role, index) => (
          <DetailRoleButton
            selected={selectedDevPositionRole}
            role={role}
            key={`ddrb-${index}`}
            index={index}
            selectDevRole={selectDevRole}
          />
        ))}
      </Box>
      )}
      </>
    )}

    <LoadingButton
      size="large"
      variant="contained"
      sx={{ mt: { xs: 6, sm : 6 } }}
      disabled={!canSubmit}
      onClick={handleSubmit(setDetailPosition)}
      loading={isSubmit}
    >
      다음
    </LoadingButton>
    {jobPost.position === 'developer' && (
      <LoadingButton
        size="large"
        variant="text"
        sx={{ mt: 2 }}
        onClick={() => setNoDeveloperDialog(true)}
        loading={isSubmit}
      >
        모르겠어요
      </LoadingButton>
    )}
    {jobPost.position !== 'developer' && (
      <LoadingButton
        size="large"
        variant="text"
        sx={{ mt: 2 }}
        onClick={handleSubmit(setDetailPosition)}
        loading={isSubmit}
      >
        건너뛰기
      </LoadingButton>
    )}

    <PositionGuideDialog
      role={getValues().role}
      isOpen={isOpenPositionGuide}
      closeDialog={() => setIsOpenPositionGuide(false)}
    />

    <JobPostHelpDialog
      jobPostId={jobPost?.id}
      open={isOpenMeetingDialog}
      closeDialog={() => setIsOpenMeetingDialog(false)}
    />

    <NoDeveloperDialog
      open={noDeveloperDialog}
      closeDialog={() => setNoDeveloperDialog(false)}
      skip={handleSubmit(setDetailPosition)}
    />

    <CrewRejectHistoryDialog
      isOpen={crewRejectHistoryOpen}
      closeDialog={() => setCrewRejectHistoryOpen(false)}
      history={jobPost?.candidate_set}
      editJobPost={null}
    />

    <ReviewAgainDialog
      open={reviewAgainOpen}
      closeDialog={() => setReviewAgainOpen(false)}
    />

    </>
  )
}

const BoldWhenSelected = styled('span', {
  shouldForwardProp: (prop) => prop !== 'selected',
})(({ selected, theme }) => ({
  ...(selected && {
    fontWeight: theme.typography.fontWeightBold,
  }),
}));

function DetailRoleButton({ selected, role, index, selectDevRole }) {
  const smUp = useResponsive('up', 'sm');
  const techLeadSelected = selected?.role === "Tech Lead";
  return (
    <Button
      disableRipple
      variant={selected?.role === role.role ? "outlined" : "text"}
      sx={{
        display: 'block',
        borderRadius: 2,
        p: 2,
        pr: 0,
        boxShadow: (theme) => theme.customShadows.card,
        ...(selected !== null && selected?.role !== role.role && {
          color: "text.disabled",
          backgroundColor: "grey.200",
        })
      }}
      onClick={() => selectDevRole(role)}
    >
      <Stack direction="row" spacing={smUp ? 2 : 1} alignItems={role?.tags?.length > 0 ? "flex-start" : "center"}>
        <Avatar
          src={role?.image_url}
          alt={role?.role}
          sx={{ width: 48, height: 48 }}
        />
        <Stack alignItems="flex-start">
          <Typography variant={"h5"} textAlign={"left"} sx={{ wordBreak: "keep-all" }}>
            {role.role}
          </Typography>
          <Stack direction="row" sx={{ gap: 0.5, flexWrap: "wrap", ...(role?.tags) && { mt: 0.5 } }}>
            {role?.tags?.split(',').map((tag, tagindex) => (
              <Chip
                key={`tag-${index}-${tagindex}`}
                variant="outlined"
                disabled={selected !== null && selected?.role !== role.role}
                color="primary"
                label={`#${tag}`}
                size="small"
              />
            ))}
          </Stack>
          {role.role === "Tech Lead" && (
            <Stack sx={{ mt: 1.5 }} spacing={1} alignItems="flex-start">
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <SvgIconStyle src={`/icons/icon-check-mono.svg`} sx={{ width: 20, height: 20, color: techLeadSelected ? "text.priamry" : "text.disabled" }} />
                <Typography variant="body2" sx={{ color: techLeadSelected ? "text.priamry" : "text.secondary" }}>
                  매주 Zoom <BoldWhenSelected selected={techLeadSelected}>주니어 멘토링</BoldWhenSelected> 세션
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <SvgIconStyle src={`/icons/icon-check-mono.svg`} sx={{ width: 20, height: 20, color: techLeadSelected ? "text.priamry" : "text.disabled" }} />
                <Typography variant="body2" sx={{ color: techLeadSelected ? "text.priamry" : "text.secondary" }}>
                  {smUp && "개발 이슈가 생길 경우, "}<BoldWhenSelected selected={techLeadSelected}>상시 Slack Q&A</BoldWhenSelected>
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <SvgIconStyle src={`/icons/icon-check-mono.svg`} sx={{ width: 20, height: 20, color: techLeadSelected ? "text.priamry" : "text.disabled" }} />
                <Typography variant="body2" sx={{ color: techLeadSelected ? "text.priamry" : "text.secondary" }}>
                  {smUp ? "개발 경험이 녹아있는 " : "고품질 "}<BoldWhenSelected selected={techLeadSelected}>GitHub 코드 리뷰</BoldWhenSelected>
                </Typography>
              </Stack>
            </Stack>
          )}
        </Stack>
      </Stack>
    </Button>
  )
};
